import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { api } from './api';

const TripBuilderContext = createContext();

const STORAGE_KEY = 'pointspilot_mockups';
const CURRENT_KEY = 'pointspilot_current_trip';
const USER_KEY = 'pointspilot_user_config';

function loadFromStorage(key, fallback) {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch { return fallback; }
}

function saveToStorage(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

const DEFAULT_USER_CONFIG = {
  travelerNames: '',
  travelers: 2,
  cabinClass: 'business',
  origin: 'JFK',
  pointsBalances: {
    amex_mr: 0,
    chase_ur: 0,
  },
  configured: false,
};

export function TripBuilderProvider({ children, trip }) {
  // User config (entered by user on setup screen)
  const [userConfig, setUserConfigState] = useState(() => {
    const saved = loadFromStorage(USER_KEY, null);
    return saved ? { ...DEFAULT_USER_CONFIG, ...saved } : DEFAULT_USER_CONFIG;
  });

  // Persist user config
  useEffect(() => {
    saveToStorage(USER_KEY, userConfig);
  }, [userConfig]);

  const setUserConfig = useCallback((updates) => {
    setUserConfigState(prev => ({ ...prev, ...updates }));
  }, []);

  const setPointsBalance = useCallback((program, balance) => {
    setUserConfigState(prev => ({
      ...prev,
      pointsBalances: { ...prev.pointsBalances, [program]: Number(balance) || 0 },
    }));
  }, []);

  const removePointsBalance = useCallback((program) => {
    setUserConfigState(prev => {
      const next = { ...prev.pointsBalances };
      delete next[program];
      return { ...prev, pointsBalances: next };
    });
  }, []);

  // Balances — prefer user config, fall back to trip API if any, else 0
  const amexTotal = userConfig.pointsBalances?.amex_mr
    ?? trip?.points_balances?.find(b => b.program === 'amex_mr')?.balance
    ?? 0;
  const chaseTotal = userConfig.pointsBalances?.chase_ur
    ?? trip?.points_balances?.find(b => b.program === 'chase_ur')?.balance
    ?? 0;

  // Current trip being built
  const [destination, setDestination] = useState(() => loadFromStorage(CURRENT_KEY, {}).destination || null);
  const [flight, setFlight] = useState(() => {
    const f = loadFromStorage(CURRENT_KEY, {}).flight || null;
    // One-time auto-heal for the cents-as-dollars taxes bug: any persisted
    // flight with absurdly high total_estimated_taxes (>$5000 for any
    // single flight is unrealistic) is treated as cents and divided by
    // 100. Safe to run repeatedly — once heal'd the field is in the
    // normal dollar range and the guard never fires again.
    if (f && Number(f.total_estimated_taxes) > 5000) {
      return { ...f, total_estimated_taxes: Math.round(Number(f.total_estimated_taxes) / 100) };
    }
    return f;
  });
  const [hotel, setHotel] = useState(() => loadFromStorage(CURRENT_KEY, {}).hotel || null);
  const [activities, setActivities] = useState(() => loadFromStorage(CURRENT_KEY, {}).activities || []);
  const [restaurants, setRestaurants] = useState(() => loadFromStorage(CURRENT_KEY, {}).restaurants || []);
  const [startDate, setStartDate] = useState(() => loadFromStorage(CURRENT_KEY, {}).startDate || '2026-09-05');
  const [endDate, setEndDate] = useState(() => loadFromStorage(CURRENT_KEY, {}).endDate || '2026-09-12');

  // Per-leg flight picks for multi-city trips. Keyed by leg index:
  //   0      = outbound (home → first stop)
  //   1..N-1 = inter-city legs (between stops)
  //   N      = return (last stop → home)
  // For round-trip use the legacy single `flight` slot — this map stays empty.
  const [flightsByLeg, setFlightsByLeg] = useState(() => {
    const saved = loadFromStorage(CURRENT_KEY, {}).flightsByLeg || {};
    // Auto-heal stale cents-as-dollars taxes the same way the single-flight
    // slot does.
    const healed = {};
    for (const [k, f] of Object.entries(saved)) {
      if (f && Number(f.total_estimated_taxes) > 5000) {
        healed[k] = { ...f, total_estimated_taxes: Math.round(Number(f.total_estimated_taxes) / 100) };
      } else {
        healed[k] = f;
      }
    }
    return healed;
  });

  // ── Multi-city trip shape ──
  // 'round_trip' — single destination, return to origin (the existing flow)
  // 'multi_city' — primary destination above + N additional stops below.
  // additionalStops[i] = { destinationId, destination, nights, hotel, activities[], restaurants[] }
  const [tripShape, setTripShapeState] = useState(() => loadFromStorage(CURRENT_KEY, {}).tripShape || 'round_trip');
  const [additionalStops, setAdditionalStops] = useState(() => loadFromStorage(CURRENT_KEY, {}).additionalStops || []);

  const setTripShape = useCallback((shape) => {
    setTripShapeState(shape);
    if (shape === 'round_trip') setAdditionalStops([]);
  }, []);

  // Auto-split a bundled destination (e.g. "Lisbon + Porto") into one primary
  // stop + one additional stop. Suggests a nights split based on the total
  // trip length: ~60% in first sub-city, ~40% in second.
  // Returns true if a split happened, false if the destination wasn't bundled.
  const splitBundleIntoStops = useCallback((dest, startDate, endDate) => {
    if (!dest || !dest.city || !dest.city.includes(' + ')) return false;
    const parts = dest.city.split(' + ').map(s => s.trim());
    const codes = dest.airport_codes || [];
    if (parts.length < 2) return false;
    const totalNights = Math.max(2, Math.round((new Date(endDate) - new Date(startDate)) / 86400000));
    const secondNights = Math.max(1, Math.round(totalNights * 0.4));
    // Build a virtual sub-destination for the second city. It references the
    // same destination row (so hotel/activity/restaurant fetches work) but
    // displays the right sub-city name + airport code.
    const secondSub = {
      ...dest,
      id: dest.id, // same DB row — sub-filter by neighborhood in pickers
      city: parts[1],
      airport_codes: codes.slice(1).length ? codes.slice(1) : [codes[codes.length - 1]],
      _bundleParent: dest.id,
      _bundleSubName: parts[1],
    };
    setTripShapeState('multi_city');
    setAdditionalStops([{
      destinationId: secondSub.id,
      destination: secondSub,
      nights: secondNights,
      hotel: null,
      activities: [],
      restaurants: [],
    }]);
    return true;
  }, []);

  const addStop = useCallback((dest, nights = 3) => {
    setAdditionalStops(prev => [...prev, {
      destinationId: dest.id,
      destination: dest,
      nights,
      hotel: null,
      activities: [],
      restaurants: [],
    }]);
  }, []);

  // Apply a prescriptive multi-city pattern from tripGuidance.js. Sets the
  // trip dates to match totalNights, sets multi-city shape, and updates
  // the additional-stop nights count to match the pattern's allocation
  // for the second sub-city. If the user already auto-split a bundled
  // destination, the existing additional stop gets its nights updated.
  const applyMultiCityPattern = useCallback((pattern, destination) => {
    if (!destination || !pattern || !pattern.nights) return;
    const cities = Object.keys(pattern.nights);
    if (cities.length < 2) return;
    setTripShapeState('multi_city');
    // Adjust trip end date to match totalNights
    const startDate = (loadFromStorage(CURRENT_KEY, {}).startDate) || '2026-09-05';
    const start = new Date(startDate + 'T12:00:00');
    const newEnd = new Date(start);
    newEnd.setDate(newEnd.getDate() + pattern.totalNights);
    setEndDate(newEnd.toISOString().slice(0, 10));
    // For bundled destinations, the second city gets the second slot
    const secondCityName = cities[1];
    const secondNights = pattern.nights[secondCityName];
    const codes = destination.airport_codes || [];
    const secondSub = {
      ...destination,
      id: destination.id,
      city: secondCityName,
      airport_codes: codes.slice(1).length ? codes.slice(1) : [codes[codes.length - 1]],
      _bundleParent: destination.id,
      _bundleSubName: secondCityName,
    };
    setAdditionalStops([{
      destinationId: secondSub.id,
      destination: secondSub,
      nights: secondNights,
      hotel: null,
      activities: [],
      restaurants: [],
    }]);
  }, []);

  const removeStop = useCallback((index) => {
    setAdditionalStops(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateStop = useCallback((index, patch) => {
    setAdditionalStops(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s));
  }, []);

  const setStopHotel = useCallback((index, hotelObj) => {
    setAdditionalStops(prev => prev.map((s, i) =>
      i === index ? { ...s, hotel: s.hotel?.id === hotelObj?.id ? null : hotelObj } : s
    ));
  }, []);

  // Bulk-set activities/restaurants for an additional stop. Used by Pilot's
  // auto-plan apply handler when multi-city activities/restaurants need to
  // land on the right stop (Porto activities go to Porto, not primary).
  const setStopActivities = useCallback((index, activities) => {
    setAdditionalStops(prev => prev.map((s, i) =>
      i === index ? { ...s, activities: activities || [] } : s
    ));
  }, []);

  const setStopRestaurants = useCallback((index, restaurants) => {
    setAdditionalStops(prev => prev.map((s, i) =>
      i === index ? { ...s, restaurants: restaurants || [] } : s
    ));
  }, []);

  const toggleStopActivity = useCallback((index, activity) => {
    setAdditionalStops(prev => prev.map((s, i) => {
      if (i !== index) return s;
      const has = s.activities.some(a => a.id === activity.id);
      return { ...s, activities: has ? s.activities.filter(a => a.id !== activity.id) : [...s.activities, activity] };
    }));
  }, []);

  const toggleStopRestaurant = useCallback((index, r) => {
    setAdditionalStops(prev => prev.map((s, i) => {
      if (i !== index) return s;
      const has = s.restaurants.some(x => x.id === r.id);
      return { ...s, restaurants: has ? s.restaurants.filter(x => x.id !== r.id) : [...s.restaurants, r] };
    }));
  }, []);

  // Combined stop list: index 0 is primary destination, then additional stops.
  // Always derives nights for the primary from start/end dates.
  const stops = (() => {
    if (!destination) return [];
    const primaryNights = Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000));
    const additionalTotal = additionalStops.reduce((s, x) => s + (x.nights || 0), 0);
    const primaryActualNights = tripShape === 'multi_city'
      ? Math.max(1, primaryNights - additionalTotal)
      : primaryNights;
    return [
      { destination, nights: primaryActualNights, hotel, activities, restaurants, primary: true },
      ...additionalStops,
    ];
  })();

  // Derived leg list for multi-city. Each leg has its label, the origin +
  // destination airport codes, and the picked flight (if any) from
  // flightsByLeg.
  //   leg 0     = outbound (home → first stop)
  //   leg 1..N  = inter-city (between consecutive stops)
  //   leg N+1   = return (last stop → home)
  const legs = (() => {
    if (tripShape !== 'multi_city' || stops.length === 0) return [];
    const home = userConfig.origin || 'JFK';
    const result = [];
    // Outbound
    result.push({
      index: 0,
      kind: 'outbound',
      label: `Outbound · ${home} → ${stops[0].destination.city}`,
      origin: home,
      destinationCodes: stops[0].destination.airport_codes || [],
      flight: flightsByLeg[0] || null,
    });
    // Inter-city — when transitioning out of a bundled "City A + City B"
    // stop, the user is in the LAST city, so depart from its airport; the
    // arrival airport is the FIRST in the next stop's codes.
    for (let i = 0; i < stops.length - 1; i++) {
      const from = stops[i].destination;
      const to = stops[i + 1].destination;
      const fromCodes = from.airport_codes || [];
      const fromCityParts = (from.city || '').split(' + ').map(s => s.trim());
      const fromCity = fromCityParts[fromCityParts.length - 1] || from.city;
      const fromOrigin = fromCodes[fromCodes.length - 1] || fromCodes[0];
      const toCityParts = (to.city || '').split(' + ').map(s => s.trim());
      const toCity = toCityParts[0] || to.city;
      result.push({
        index: i + 1,
        kind: 'intercity',
        label: `Leg ${i + 2} · ${fromCity} → ${toCity}`,
        origin: fromOrigin,
        destinationCodes: to.airport_codes || [],
        flight: flightsByLeg[i + 1] || null,
      });
    }
    // Return
    // For an un-split bundled destination (city = "Lisbon + Porto",
    // airport_codes = ["LIS", "OPO"]), the trip ends at the LAST city in
    // the bundle, not the first. The return must depart from there
    // (OPO → JFK), not the bundle's primary airport (LIS → JFK). Same
    // logic applies to outbound (use first city/first code, already
    // correct above since stops[0] is naturally the start of the trip).
    const lastStop = stops[stops.length - 1].destination;
    const lastCityParts = (lastStop.city || '').split(' + ').map(s => s.trim());
    const lastCityName = lastCityParts[lastCityParts.length - 1] || lastStop.city;
    const lastCodes = lastStop.airport_codes || [];
    const lastOrigin = lastCodes[lastCodes.length - 1] || lastCodes[0];
    const returnIdx = stops.length;
    result.push({
      index: returnIdx,
      kind: 'return',
      label: `Return · ${lastCityName} → ${home}`,
      origin: lastOrigin,
      destinationCodes: [home],
      flight: flightsByLeg[returnIdx] || null,
    });
    return result;
  })();

  // Saved mockup trips for comparison
  const [mockups, setMockups] = useState(() => loadFromStorage(STORAGE_KEY, []));

  // Persist current trip to localStorage
  useEffect(() => {
    saveToStorage(CURRENT_KEY, { destination, flight, hotel, activities, restaurants, startDate, endDate, tripShape, additionalStops, flightsByLeg });
  }, [destination, flight, hotel, activities, restaurants, startDate, endDate, tripShape, additionalStops, flightsByLeg]);

  // Auto-split bundled destinations when entering multi-city mode. Without
  // this, a user who picks "Lisbon + Porto" then toggles multi-city sees a
  // single un-split stop — per-stop hotel/activity/restaurant pickers never
  // appear because tb.stops.length stays at 1, and the return-leg airport
  // defaults to LIS instead of OPO. The split builds the second virtual
  // sub-stop (Porto) automatically so the rest of the UI iterates correctly.
  // AUTO multi-city detection: if user picks a bundled destination
  // ("Lisbon + Porto") in round-trip mode, automatically flip to
  // multi-city and split into stops. User shouldn't have to manually
  // toggle multi-city when the destination name literally has " + " in it.
  useEffect(() => {
    if (destination?.city?.includes(' + ') && tripShape !== 'multi_city') {
      console.log('[auto-multi-city] Bundled destination detected, switching to multi-city mode');
      setTripShapeState('multi_city');
    }
  }, [destination, tripShape]);

  useEffect(() => {
    if (tripShape !== 'multi_city') return;
    if (!destination?.city?.includes(' + ')) return;
    if (additionalStops.length > 0) return;
    splitBundleIntoStops(destination, startDate, endDate);
  }, [tripShape, destination, additionalStops.length, startDate, endDate, splitBundleIntoStops]);

  // Debounced sync of multi-city state to the backend. Without this the
  // share endpoints + calendar export only see the legacy single-flight
  // /single-hotel slots from trip_selections — they'd drop additional
  // stops + per-leg flights entirely. We only fire on multi-city-relevant
  // changes to keep PATCH traffic low.
  const syncTimer = useRef(null);
  useEffect(() => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      const payload = {
        multi_city_state: {
          tripShape,
          destination: destination ? { id: destination.id, city: destination.city, airport_codes: destination.airport_codes } : null,
          additionalStops: additionalStops.map(s => ({
            destinationId: s.destinationId,
            destination: s.destination ? { id: s.destination.id, city: s.destination.city, airport_codes: s.destination.airport_codes, _bundleParent: s.destination._bundleParent, _bundleSubName: s.destination._bundleSubName } : null,
            nights: s.nights,
            hotel: s.hotel ? { id: s.hotel.id, hotel_name: s.hotel.hotel_name, hotel_program: s.hotel.hotel_program, total_points: s.hotel.total_points, neighborhood: s.hotel.neighborhood, source_program: s.hotel.source_program, nights: s.hotel.nights } : null,
            activities: (s.activities || []).map(a => ({ id: a.id, name: a.name, category: a.category, price_for_two: a.price_for_two, duration: a.duration })),
            restaurants: (s.restaurants || []).map(r => ({ id: r.id, name: r.name, cuisine: r.cuisine, price_range: r.price_range, avg_meal_for_two: r.avg_meal_for_two })),
          })),
          flightsByLeg,
          startDate, endDate,
        },
      };
      // PATCH the trip — best-effort, silent on failure (network blips
      // shouldn't disrupt the user; next mutation will retry the sync).
      api.updateTrip(payload).catch(() => {});
    }, 1200);
    return () => syncTimer.current && clearTimeout(syncTimer.current);
  }, [tripShape, destination, additionalStops, flightsByLeg, startDate, endDate]);

  // Persist mockups
  useEffect(() => {
    saveToStorage(STORAGE_KEY, mockups);
  }, [mockups]);

  // ── Computed costs ──
  // All seed/live data is priced "for two travelers" (legacy convention).
  // Scale to the user's actual traveler count. Hotels are per-room, so they
  // do NOT scale with traveler count (a room is the same price for 1 or 2).
  //
  // Multi-city: activity / restaurant cash and hotel points sum across the
  // primary stop AND every additional stop, so the running budget tracks
  // the actual end-to-end trip cost — not just the first city.
  const travelers = userConfig.travelers || 2;
  const travelerScale = travelers / 2;
  // For round trip: single picked flight × travelerScale.
  // For multi-city: sum of every leg's picked flight (each holds a per-2
  // value just like the legacy slot), then × travelerScale.
  const legFlightsArr = Object.values(flightsByLeg).filter(Boolean);
  const flightPoints = tripShape === 'multi_city'
    ? Math.round(legFlightsArr.reduce((s, f) => s + (f.total_points_two_travelers || 0), 0) * travelerScale)
    : Math.round((flight?.total_points_two_travelers || 0) * travelerScale);
  const flightTaxes = tripShape === 'multi_city'
    ? Math.round(legFlightsArr.reduce((s, f) => s + (f.total_estimated_taxes || 0), 0) * travelerScale)
    : Math.round((flight?.total_estimated_taxes || 0) * travelerScale);

  // Hotel points: primary + all additional stops with picked hotels
  const additionalHotelPoints = additionalStops.reduce((s, st) => s + (st.hotel?.total_points || 0), 0);
  const hotelPoints = (hotel?.total_points || 0) + additionalHotelPoints;

  // Activity cash: primary + all additional stops' activities
  const additionalActivityCashRaw = additionalStops.reduce(
    (s, st) => s + (st.activities || []).reduce((a, x) => a + (x.price_for_two || 0), 0),
    0
  );
  const activityCash = Math.round(
    (activities.reduce((s, a) => s + (a.price_for_two || 0), 0) + additionalActivityCashRaw) * travelerScale
  );

  // Restaurant cash: same pattern
  const additionalRestaurantCashRaw = additionalStops.reduce(
    (s, st) => s + (st.restaurants || []).reduce((a, x) => a + (x.avg_meal_for_two || 0), 0),
    0
  );
  const restaurantCash = Math.round(
    (restaurants.reduce((s, r) => s + (r.avg_meal_for_two || 0), 0) + additionalRestaurantCashRaw) * travelerScale
  );

  const totalCash = flightTaxes + activityCash + restaurantCash;
  const totalPoints = flightPoints + hotelPoints;
  const amexRemaining = amexTotal - flightPoints;
  const chaseRemaining = chaseTotal - hotelPoints;

  // itemCount: count everything across all stops so the summary chip is right
  const additionalItemCount = additionalStops.reduce(
    (s, st) => s + (st.hotel ? 1 : 0) + (st.activities?.length || 0) + (st.restaurants?.length || 0),
    0
  );
  const itemCount =
    (destination ? 1 : 0) +
    (flight ? 1 : 0) +
    (hotel ? 1 : 0) +
    activities.length +
    restaurants.length +
    additionalStops.length + // extra-city destinations themselves count
    additionalItemCount;

  // ── Actions ──
  const pickDestination = useCallback((dest) => {
    setDestination(dest);
    setFlight(null);
    setHotel(null);
    setActivities([]);
    setRestaurants([]);
    // If the destination is bundled ("Lisbon + Porto", "Amalfi Coast + Rome",
    // etc.) auto-split into multi-city stops with a 60/40 nights suggestion.
    // The user can still manually flip back to round-trip from Step 1, but
    // this is the right default — they obviously want both cities.
    if (dest?.city?.includes(' + ') && (dest.airport_codes || []).length >= 1) {
      const parts = dest.city.split(' + ').map(s => s.trim());
      const codes = dest.airport_codes || [];
      if (parts.length >= 2) {
        const totalNights = Math.max(2, Math.round((new Date(endDate) - new Date(startDate)) / 86400000));
        const secondNights = Math.max(1, Math.round(totalNights * 0.4));
        const secondSub = {
          ...dest,
          id: dest.id,
          city: parts[1],
          airport_codes: codes.slice(1).length ? codes.slice(1) : [codes[codes.length - 1]],
          _bundleParent: dest.id,
          _bundleSubName: parts[1],
        };
        setTripShapeState('multi_city');
        setAdditionalStops([{
          destinationId: secondSub.id,
          destination: secondSub,
          nights: secondNights,
          hotel: null,
          activities: [],
          restaurants: [],
        }]);
      }
    } else {
      // Picking a single-city destination resets any prior multi-city stops
      // (so going Lisbon+Porto → Paris doesn't leave Porto stuck as a stop).
      setAdditionalStops([]);
    }
  }, [startDate, endDate]);

  const pickFlight = useCallback((f) => {
    setFlight(prev => prev?.id === f?.id ? null : f);
  }, []);

  // Create a synthetic flight object from live seats.aero availability data.
  // The seats.aero per-person numbers get scaled by the user's traveler
  // count, but stored on the field that the rest of the app reads
  // (total_points_two_travelers) divided back to a 2-traveler basis so the
  // global travelerScale logic in this file applies it correctly.
  // Build a synthetic flight object from a seats.aero live result. Pure —
  // doesn't mutate state. Used by both the legacy single-flight picker and
  // the new per-leg picker.
  const buildSynthFlight = useCallback((liveResult, selectedCabin = 'business') => {
    const isFirst = selectedCabin === 'first';
    const mileageCost = isFirst ? liveResult.firstMiles : liveResult.businessMiles;
    const taxes = isFirst ? (liveResult.firstTaxes || 0) : (liveResult.businessTaxes || 0);
    return {
      id: `live-${liveResult.id || Date.now()}`,
      airline_program: liveResult.source ? (liveResult.source.charAt(0).toUpperCase() + liveResult.source.slice(1)) : 'Unknown',
      total_points_two_travelers: (mileageCost || 0) * 2,
      total_estimated_taxes: taxes * 2,
      routing_description: `${liveResult.route?.OriginAirport || '???'} → ${liveResult.route?.DestinationAirport || '???'}`,
      cabin_class: isFirst ? 'first' : 'business',
      source_program: 'amex_mr',
      flight_duration_outbound: null,
      is_nonstop: false,
      num_stops: null,
      aircraft_type: null,
      seat_product: isFirst ? 'First Class (Live)' : 'Business Class (Live)',
      cents_per_point: null,
      _liveSource: true,
      _source: liveResult.source,
    };
  }, []);

  const pickFlightFromLive = useCallback((liveResult, selectedCabin = 'business') => {
    const synth = buildSynthFlight(liveResult, selectedCabin);
    setFlight(prev => prev?.id === synth.id ? null : synth);
  }, [buildSynthFlight]);

  // Pick a flight for a SPECIFIC leg index on a multi-city trip. The same
  // result clicked twice un-picks (toggle behavior). Used by per-leg
  // LiveAvailability widgets.
  const pickFlightForLeg = useCallback((legIndex, liveResult, selectedCabin = 'business') => {
    const synth = buildSynthFlight(liveResult, selectedCabin);
    setFlightsByLeg(prev => {
      const next = { ...prev };
      if (next[legIndex]?.id === synth.id) {
        delete next[legIndex];
      } else {
        next[legIndex] = synth;
      }
      return next;
    });
  }, [buildSynthFlight]);

  const clearLegFlight = useCallback((legIndex) => {
    setFlightsByLeg(prev => {
      const next = { ...prev };
      delete next[legIndex];
      return next;
    });
  }, []);

  const pickHotel = useCallback((h) => {
    setHotel(prev => prev?.id === h?.id ? null : h);
  }, []);

  const toggleActivity = useCallback((a) => {
    setActivities(prev =>
      prev.some(x => x.id === a.id) ? prev.filter(x => x.id !== a.id) : [...prev, a]
    );
  }, []);

  const toggleRestaurant = useCallback((r) => {
    setRestaurants(prev =>
      prev.some(x => x.id === r.id) ? prev.filter(x => x.id !== r.id) : [...prev, r]
    );
  }, []);

  const setDates = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Computed nights
  const nights = startDate && endDate
    ? Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))
    : 7;

  const clearTrip = useCallback(() => {
    setDestination(null);
    setFlight(null);
    setHotel(null);
    setActivities([]);
    setRestaurants([]);
    setStartDate('2026-09-05');
    setEndDate('2026-09-12');
  }, []);

  // ── Mockup management ──
  const saveMockup = useCallback((name) => {
    if (!destination) return;
    const mockup = {
      id: Date.now(),
      name: name || `${destination.city} Trip`,
      savedAt: new Date().toISOString(),
      destination,
      flight,
      hotel,
      activities,
      restaurants,
      startDate, endDate, nights,
      // Snapshot costs
      costs: { flightPoints, flightTaxes, hotelPoints, activityCash, restaurantCash, totalCash, totalPoints, amexRemaining, chaseRemaining, itemCount, nights },
    };
    setMockups(prev => [...prev, mockup]);
    return mockup;
  }, [destination, flight, hotel, activities, restaurants, startDate, endDate, nights, flightPoints, flightTaxes, hotelPoints, activityCash, restaurantCash, totalCash, totalPoints, amexRemaining, chaseRemaining, itemCount]);

  const loadMockup = useCallback((mockup) => {
    setDestination(mockup.destination);
    setFlight(mockup.flight);
    setHotel(mockup.hotel);
    setActivities(mockup.activities);
    setRestaurants(mockup.restaurants);
    if (mockup.startDate) setStartDate(mockup.startDate);
    if (mockup.endDate) setEndDate(mockup.endDate);
  }, []);

  const deleteMockup = useCallback((id) => {
    setMockups(prev => prev.filter(m => m.id !== id));
  }, []);

  const value = {
    // User config
    userConfig, setUserConfig, setPointsBalance, removePointsBalance,
    // State
    destination, flight, hotel, activities, restaurants,
    startDate, endDate, nights, setDates,
    // Costs
    amexTotal, chaseTotal,
    flightPoints, flightTaxes, hotelPoints,
    activityCash, restaurantCash, totalCash, totalPoints,
    amexRemaining, chaseRemaining, itemCount,
    travelers, travelerScale,
    // Helpers — every seed/live value is "for 2 travelers" by legacy
    // convention. These scale to the actual party size.
    scaleForTravelers: (valueForTwo) => Math.round((valueForTwo || 0) * travelerScale),
    travelersLabel: `${travelers} traveler${travelers === 1 ? '' : 's'}`,
    // Actions
    pickDestination, pickFlight, pickFlightFromLive, pickHotel, toggleActivity, toggleRestaurant, clearTrip,
    splitBundleIntoStops, applyMultiCityPattern,
    // Multi-city
    tripShape, setTripShape,
    additionalStops, addStop, removeStop, updateStop,
    setStopHotel, setStopActivities, setStopRestaurants, toggleStopActivity, toggleStopRestaurant,
    stops, legs,
    flightsByLeg, pickFlightForLeg, clearLegFlight,
    isMultiCity: tripShape === 'multi_city',
    // Mockups
    mockups, saveMockup, loadMockup, deleteMockup,
  };

  return (
    <TripBuilderContext.Provider value={value}>
      {children}
    </TripBuilderContext.Provider>
  );
}

export function useTripBuilder() {
  const ctx = useContext(TripBuilderContext);
  if (!ctx) throw new Error('useTripBuilder must be used within TripBuilderProvider');
  return ctx;
}
